import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select } from 'antd';
import { useSelector } from 'react-redux';
import { getStockAgeRevenue } from '../../services/rapportService';
import {
  NdoePage, NdoePageHeader, NdoePeriodPicker,
  NdoeResponsiveTable, NdoeStats, NdoeStatCard, NdoeError, NdoeLoading,
} from '../../components/ndoe';
import { money, pct } from './format';
import './rapports.css';

// Barre proportionnelle « part du CA » (or). Largeur = part %, plafonnée visuellement.
const PartBar = ({ value }) => (
  <div className="anc-partbar" title={`${value} % du CA`}>
    <div className="anc-partbar__track">
      <i style={{ width: `${Math.max(2, Math.min(100, Number(value) || 0))}%` }} />
    </div>
    <span className="num">{value} %</span>
  </div>
);

const anneeLabel = (a) => (a === 'inconnu' ? 'Avant le suivi' : a);
const MOIS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
// '2026-07' → « juillet 2026 »
const moisLabel = (m) => {
  const [y, mm] = String(m || '').split('-');
  const idx = Number(mm) - 1;
  return MOIS_FR[idx] ? `${MOIS_FR[idx]} ${y}` : m;
};

export default function AnalyseAnciennete() {
  const user = useSelector((s) => s.user?.currentUser);
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';

  const [period, setPeriod] = useState({ period: 90 });
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const apiParams = useMemo(() => ({
    period: period.period === 'custom' ? undefined : period.period,
    start_date: period.period === 'custom' ? period.start_date : undefined,
    end_date: period.period === 'custom' ? period.end_date : undefined,
  }), [period]);

  const load = useCallback(async () => {
    // « Perso » sélectionné mais dates pas encore choisies : on ne recharge pas
    // (sinon un GET sans paramètre renverrait la fenêtre par défaut du serveur).
    if (period.period === 'custom' && !(period.start_date && period.end_date)) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await getStockAgeRevenue(apiParams);
      setPayload(data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || "Impossible de charger le rapport d'ancienneté du stock.");
    } finally { setLoading(false); }
  }, [apiParams, period]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const total = payload?.total || {};
  const fenetres = useMemo(() => payload?.fenetres || [], [payload]);
  const millesimes = useMemo(() => payload?.millesimes || [], [payload]);
  const aInconnu = [...fenetres, ...millesimes].some((r) => r.key === 'inconnu' || r.annee === 'inconnu');

  // Vue éclatée par MOIS (demande 18/07) : cliquer une année la déplie en lignes
  // mensuelles (tendance des référencements récents). Les années récentes avec
  // détail sont dépliées d'office — c'est la question posée au rapport.
  const [deplies, setDeplies] = useState(null);   // Set des années dépliées (null = défaut)
  const depliesEffectif = useMemo(() => {
    if (deplies) return deplies;
    const d = new Set();
    const recente = millesimes.find((m) => m.annee !== 'inconnu' && (m.detail_mois || []).length > 1);
    if (recente) d.add(recente.annee);
    return d;
  }, [deplies, millesimes]);
  const basculerAnnee = (annee) => {
    const d = new Set(depliesEffectif);
    if (d.has(annee)) d.delete(annee); else d.add(annee);
    setDeplies(d);
  };
  // Tris décisionnels (demande 19/07) : classer les millésimes — et leurs mois
  // dépliés — par performance, pour décider quelle génération de stock pousse.
  // « Avant le suivi » reste TOUJOURS en fin (peu importe ses montants) et les
  // valeurs absentes (marge inconnue) passent en dernier.
  const [tri, setTri] = useState('annee');
  const TRIS = useMemo(() => {
    const parChamp = (champ) => (a, b) => {
      const va = a[champ]; const vb = b[champ];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;                 // inconnu → en fin
      if (vb == null) return -1;
      return vb - va;                           // décroissant
    };
    return {
      annee: null,                              // ordre chronologique du serveur
      ca: parChamp('ca'),
      marge: parChamp('marge'),
      taux_marge: parChamp('taux_marge'),
      paires: parChamp('paires'),
      part_ca: parChamp('part_ca'),
    };
  }, []);
  const lignesMillesime = useMemo(() => {
    const cmp = TRIS[tri] || null;
    const annees = [...millesimes];
    if (cmp) {
      annees.sort((a, b) => {
        if (a.annee === 'inconnu') return 1;    // « Avant le suivi » ferme la marche
        if (b.annee === 'inconnu') return -1;
        return cmp(a, b);
      });
    }
    return annees.flatMap((m) => {
      const detail = m.detail_mois || [];
      if (!depliesEffectif.has(m.annee) || !detail.length) return [m];
      const mois = cmp ? [...detail].sort(cmp) : detail;   // mois triés comme les années
      return [m, ...mois.map((x) => ({ ...x, is_mois: true }))];
    });
  }, [millesimes, depliesEffectif, tri, TRIS]);

  const colsFenetre = [
    { title: 'Ancienneté d’entrée', dataIndex: 'label', key: 'label', render: (v) => <b>{v}</b> },
    { title: 'Part du CA', key: 'part', render: (t, r) => <PartBar value={r.part_ca} /> },
    { title: 'CA', dataIndex: 'ca', key: 'ca', align: 'right', render: (v) => <span className="num">{money(v)}</span> },
    { title: 'Paires', dataIndex: 'paires', key: 'paires', align: 'right', render: (v) => <span className="num">{v}</span> },
    { title: 'Marge', dataIndex: 'marge', key: 'marge', align: 'right', render: (v) => <span className="num">{v == null ? '—' : money(v)}</span> },
    { title: 'Taux de marge', dataIndex: 'taux_marge', key: 'tm', align: 'right', render: (v) => <span className="num">{v == null ? '—' : pct(v)}</span> },
  ];
  const colsMillesime = [
    {
      title: 'Année de 1re entrée', dataIndex: 'annee', key: 'annee',
      render: (v, r) => {
        if (r.is_mois) return <span className="anc-mois">{moisLabel(r.mois)}</span>;
        const detail = r.detail_mois || [];
        if (!detail.length) return <b>{anneeLabel(v)}</b>;
        const ouvert = depliesEffectif.has(v);
        return (
          <button type="button" className="anc-annee" onClick={() => basculerAnnee(v)}
            title={ouvert ? 'Replier le détail mensuel' : 'Voir le détail mois par mois'}>
            <span className={`anc-caret ${ouvert ? 'is-ouvert' : ''}`} aria-hidden="true">▸</span>
            <b>{anneeLabel(v)}</b>
          </button>
        );
      },
    },
    { title: 'Modèles', dataIndex: 'nb_modeles', key: 'nbm', align: 'right', render: (v) => <span className="num">{v ?? '—'}</span> },
    { title: 'Paires', dataIndex: 'paires', key: 'paires', align: 'right', render: (v) => <span className="num">{v}</span> },
    { title: 'CA', dataIndex: 'ca', key: 'ca', align: 'right', render: (v) => <span className="num">{money(v)}</span> },
    { title: 'Part du CA', key: 'part', render: (t, r) => <PartBar value={r.part_ca} /> },
    { title: 'Marge', dataIndex: 'marge', key: 'marge', align: 'right', render: (v) => <span className="num">{v == null ? '—' : money(v)}</span> },
    { title: 'Taux de marge', dataIndex: 'taux_marge', key: 'tm', align: 'right', render: (v) => <span className="num">{v == null ? '—' : pct(v)}</span> },
  ];

  if (!isAdmin) {
    return (
      <NdoePage>
        <NdoePageHeader eyebrow="Rapports" title="CA par ancienneté du stock" />
        <NdoeError title="Accès réservé" text="Ce rapport est réservé à l'administration." />
      </NdoePage>
    );
  }

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Rapports"
        title="CA par ancienneté du stock"
        subtitle="Quelle part de votre chiffre d'affaires vient de modèles récemment référencés, et lesquels rapportent le plus — par millésime d'entrée en stock."
      />

      <div className="anc-toolbar">
        <NdoePeriodPicker value={period} onChange={setPeriod} />
        <Select value={tri} onChange={setTri} className="anc-tri" title="Trier les millésimes"
          options={[
            { value: 'annee', label: 'Tri : année (récent d’abord)' },
            { value: 'ca', label: 'Plus gros CA' },
            { value: 'marge', label: 'Plus grosse marge' },
            { value: 'taux_marge', label: 'Meilleur taux de marge' },
            { value: 'paires', label: 'Plus de paires' },
            { value: 'part_ca', label: 'Plus grosse part du CA' },
          ]} />
      </div>

      {error ? <NdoeError title="Erreur" text={error} onRetry={load} />
        : loading ? <NdoeLoading rows={5} />
        : (
          <>
            <NdoeStats>
              <NdoeStatCard label="Chiffre d'affaires (période)" value={money(total.ca)} gold />
              <NdoeStatCard label="Marge estimée" value={total.marge == null ? '—' : money(total.marge)} />
              <NdoeStatCard label="Taux de marge" value={total.taux_marge == null ? '—' : pct(total.taux_marge)} />
            </NdoeStats>

            <div className="ndoe-card anc-block">
              <div className="ndoe-section__title">Fraîcheur du CA — part par ancienneté d'entrée</div>
              <p className="anc-hint">Sur la période choisie, d'où vient votre CA selon l'ancienneté du premier référencement du modèle.</p>
              <NdoeResponsiveTable
                data={fenetres}
                columns={colsFenetre}
                rowKey="key"
                pagination={false}
                scroll={{ x: 400 }}
                empty={{ icon: '📦', title: 'Aucune vente sur la période' }}
              />
            </div>

            <div className="ndoe-card anc-block">
              <div className="ndoe-section__title">Millésime — CA &amp; rentabilité par année de premier référencement</div>
              <p className="anc-hint">Les modèles regroupés par l'année où ils sont entrés en stock pour la première fois.</p>
              <NdoeResponsiveTable
                data={lignesMillesime}
                columns={colsMillesime}
                rowKey={(r) => (r.is_mois ? `m-${r.mois}` : `a-${r.annee}`)}
                pagination={false}
                scroll={{ x: 400 }}
                onRow={(r) => (r.is_mois ? { className: 'anc-ligne-mois' } : {})}
                empty={{ icon: '🗓️', title: 'Aucune vente sur la période' }}
              />
            </div>

            <p className="anc-note">
              <b>Base :</b> ancienneté = depuis le <b>premier référencement</b> du modèle (sa 1re entrée en stock).
              Un modèle ancien réassorti récemment reste donc « ancien ».
              {aInconnu && <> La ligne <b>« Avant le suivi »</b> regroupe les modèles vendus sans réception enregistrée (stock d'avant le suivi des entrées, mars 2024).</>}
              {total.cout_partiel && <> La marge est <b>partielle</b> : le coût d'achat manque sur certains modèles.</>}
            </p>
          </>
        )}
    </NdoePage>
  );
}
